import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { IonGrid, IonRow, IonCol, IonItem, IonButton, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonInput, IonTextarea, IonLabel, IonButtons, IonSelect, IonSelectOption, IonIcon, IonToolbar, IonReorderGroup, IonReorder, IonModal, IonCardSubtitle, IonHeader, IonPage, IonContent, IonBackButton, IonTitle, IonFooter, useIonViewDidEnter } from '@ionic/react';
import { AppContext } from 'context/app.context';
import { Page, PHQVersion } from 'models/questionnaire.model';
import { useHistory, useParams } from 'react-router';
import { updatePHQ, updatePHQPage, deletePHQPage, reorderPages, createPHQPage, searchPages, searchVersions } from 'misc/phqHTTP';
import { addOutline, appsOutline, chevronDownOutline, chevronForwardOutline, closeCircleOutline, cloudUploadOutline, codeSlashOutline, createOutline, eyeOutline, gitBranchOutline, trashOutline } from 'ionicons/icons';
import { QuestionnaireActionTypes } from 'reducers/questionnaire.reducer';
import '../questionnaire/PHQForm.css';
import FadeIn from '../animations/fadeIn';
import PHQPublishVersionForm from './PHQPublishVersionForm';
import PHQVersionList from './PHQVersionList';
import PHQViewModeButton from './PHQViewModeButton';
import { HttpStatusCodeEnum } from 'models/http-status-code.enum';
import RemoveAlert from '../UI/RemoveAlert';
import Footer from 'layout/footer';
import LambdaEditor from './LambdaEditor';
import { AuthContext } from 'context/auth.context';
import { ROLES } from 'models/roles.enum';


const PHQPage = () => {
    const history = useHistory();
    const { logInfo } = useContext(AuthContext);
    const { phqId } = useParams<{phqId: string}>();
    const { questionnaire, updateQuestionnaire, setIsLoading, notifyError, previewPHQ } = useContext(AppContext);;
    const [versions, setVersions] = useState<PHQVersion[]>([]);
    const [publishedVer, setPublishedVer] = useState<PHQVersion | null>(null);
    const [currentVer, setCurrentVer] = useState<PHQVersion | null>(null);
    const [isOpenModalVersion, updateIsModalVersion] = useState(false);
    const [isOpenModalNewVersion, updateIsModalNewVersion] = useState(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [selectedPage, setSelectedPage] = useState<Page | null>(null);
    const [showPhqSelectedModal, setShowPhqSelectedModal] = useState(false);
    const [showLambdaEditorModal, setShowLambdaEditorModal] = useState(false);
    
    const isViewMode = questionnaire.ViewMode;
    const ionContent = useRef<HTMLIonContentElement>(null);

    const routeToPage = (page: any) => {
        updateQuestionnaire(QuestionnaireActionTypes.PAGE_SELECTED, page);
        history.push(`/phq/pages/${page.PageID}`);
    }

    const addNewPage = () => {
        if (questionnaire.PhqSelected?.Pages!.length > 0) {
            const unChangedPages = questionnaire.PhqSelected.Pages!.find(f => f.Name === 'New Page')
            if (unChangedPages) {
                notifyError("New Page already exists");
                return;
            }
        }

        setIsLoading(true);
        
        const page = new Page();
        page.Collapsed = true;
        page.PHQID = +phqId;
        page.Label = "";
        page.Priority = ((questionnaire.PhqSelected?.Pages?.length! || 0) + 1) * 10;
        createPHQPage(page)
        .then(res => {
            if (res.status === HttpStatusCodeEnum.CREATED) {
                updateQuestionnaire(QuestionnaireActionTypes.ADD_PAGE, res.data.result);
                setTimeout(() => {
                    ionContent.current?.scrollToBottom(300);
                }, 300);
            }
        })
        .finally(() => setIsLoading(false))
    }

    const updateCollapse = (page: Page) => {
        updateQuestionnaire(QuestionnaireActionTypes.UPDATE_COLLAPSE, page);
    }

    const deletePage = () => {
        setIsLoading(true);
        deletePHQPage(selectedPage!.PageID)
        .then(res => {
            if (res.status === HttpStatusCodeEnum.OK) {
                updateQuestionnaire(QuestionnaireActionTypes.DELETE_PAGE, selectedPage);
            }
        })
        .finally(() => setIsLoading(false))
    }

    const doReorder = useCallback((event: any) => {
        event.detail.complete();
        const itemMove = questionnaire!.PhqSelected!.Pages!.splice(event.detail.from, 1)[0];
        questionnaire!.PhqSelected!.Pages!.splice(event.detail.to, 0, itemMove);
        questionnaire!.PhqSelected!.Pages?.map((p, index) => {
            p.Priority = (index + 1) * 10;
        })
        event.detail.complete();
        updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PHQ, questionnaire!.PhqSelected);
        reorderPages(questionnaire!.PhqSelected!.Pages!).then(res => {});
    }, [questionnaire, updateQuestionnaire])


    const updatePage = (e: any, page: any) => {
        if (e.target.id) {
            const propName = e.target.id;
            const val = e.target.value;
            if(val !== "") {
                page[propName] = isNaN(val) ? val : +val;
                updatePHQPage(page).then(res => {
                    if (res.data && res.data.result) {
                        const updatedPage = res.data.result as Page;
                        updatedPage.Collapsed = true;
                        updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PAGE, updatedPage);
                    }
                })
            }
            else {
                const prevVal = page[propName];
                page[propName] = prevVal;
                updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PAGE, page);
            }
        }
    }

    const updatePHQ_blur = (e: any, phq: any) => {
        if (e && e.target && e.target.id) {
            const propName = e.target.id;
            phq[propName] = e.target.value;
            updatePHQ(phq).then(res => {
                updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PHQ, res.data.result);
            })
        }
    }

    const loadPages = () => {        
        searchPages({ PHQID: phqId })
        .then(res => {
            const pages = res.data.result;
            pages.length > 0
                ? updateQuestionnaire(QuestionnaireActionTypes.SET_PAGES, pages)
                : addNewPage();
        })
        .finally(() => setIsLoading(false))
    }

    const ModalPHQVersion = useCallback(() => {
        return (
            <IonModal isOpen={isOpenModalVersion}>
                <PHQVersionList fnToHideForm={(r) => {
                    setTimeout(() => {
                        updateIsModalVersion(r)
                    }, 800);
                }} versions={versions} />
            </IonModal>
        )
    }, [isOpenModalVersion, versions])

    const getPublishedVer = () => {
        return publishedVer 
            ? (<strong>Published Version: {publishedVer!.Description}</strong>) 
            : (<strong>Published Version will show up here</strong>);
    }

    const setFormVersions = () => {
        searchVersions({ PHQID: phqId })
            .then(res => {
                const versions = res.data.result;
                const published = versions.find(v => v.IsPublished) || null;
                const current = versions.find(v => v.IsCurrent) || null;
                setVersions(versions);
                setPublishedVer(published);
                setCurrentVer(current);
            })
            .finally(() => setIsLoading(false))
    }

    const onDeletePage = (e: any, page: Page) => {
        e.stopPropagation();
        setSelectedPage(page); 
        setShowDeleteAlert(true);
    } 

    useIonViewDidEnter(() => {
        setIsLoading(true);
        loadPages();
        setFormVersions();
    });

    useEffect(() => {
        // hack to get around missing data in context on page reload
        if(!questionnaire.PhqSelected) {
            history.push('/home')
        }
    }, [questionnaire.PhqSelected, history])

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar color='primary' className='ion-no-border'>
                    <IonTitle className="ion-text-center"> 
                        <div className="pointer" onClick={() => {
                                setShowPhqSelectedModal(true)
                            }}>
                            {questionnaire?.PhqSelected?.Label}
                            &nbsp;
                            <IonIcon slot="end" icon={createOutline} />
                        </div>
                    </IonTitle>
                </IonToolbar>
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonBackButton defaultHref="/home" />
                    </IonButtons>
                    <IonItem lines='none'>
                        <IonLabel slot="end">{getPublishedVer()}</IonLabel>
                        {isViewMode && (<PHQViewModeButton />)}
                        <IonButton slot="end" expand="block" size="small" onClick={() => { updateIsModalNewVersion(true) }}>
                            <IonIcon slot="end" icon={cloudUploadOutline} />
                            <strong>Publish Version</strong>
                        </IonButton>
                        <IonButton slot="end" expand="block" size="small" onClick={() => { updateIsModalVersion(true) }}>
                            <IonIcon slot="end" icon={gitBranchOutline} />
                            <strong>Versions</strong>
                        </IonButton>
                        <ModalPHQVersion />
                        <IonButton slot="end" expand="block" size="small" onClick={() => { previewPHQ(currentVer!.PHQID) }}>
                            <IonIcon slot="end" icon={eyeOutline} />
                            <strong>Preview</strong>
                        </IonButton>
                        <IonButton slot="end" expand="block" size="small" onClick={ addNewPage }>
                            <IonIcon slot="end" icon={addOutline} />
                            <strong>New Page</strong>
                        </IonButton>
                    </IonItem>
                </IonToolbar>
            </IonHeader>
            <IonContent scrollEvents={true} ref={ionContent}>
                    {isOpenModalNewVersion &&
                        <div className="w-75 mx-auto">
                            <FadeIn>
                                <PHQPublishVersionForm fnToHideForm={(published) => { updateIsModalNewVersion(false); published && setFormVersions(); }} />
                            </FadeIn>
                        </div>
                    }
                            { (questionnaire.PhqSelected?.Pages || []).length > 0 
                            ? (
                                <IonReorderGroup disabled={false} onIonItemReorder={(e: any) => { doReorder(e) }}>
                                {(questionnaire.PhqSelected?.Pages || []).map(page => {
                                    return (
                                        (
                                            <IonCard key={page.PageID} className='phq-page-card mx-auto'>
                                                <IonCardHeader className="ion-no-padding">
                                                    <IonGrid>
                                                        <IonRow className="ion-no-padding">
                                                            <IonCol size='8' className="ion-no-padding">
                                                                <IonItem onClick={(event) => { updateCollapse(page); event.stopPropagation() }} className='page-name ion-no-padding pointer' lines='none'>
                                                                    <IonButton className="ipro-btn" color="primary" fill='clear'>
                                                                        <IonIcon slot="icon-only" icon={page.Collapsed ? chevronDownOutline : chevronForwardOutline} />
                                                                    </IonButton>
                                                                    <IonLabel className='ion-no-margin'>
                                                                        <IonCardTitle><h1><strong>{page.Name}</strong></h1></IonCardTitle>
                                                                        <IonCardSubtitle color="primary" className="ion-text-wrap"><h3>{page.Label}</h3></IonCardSubtitle>
                                                                    </IonLabel>
                                                                </IonItem>
                                                            </IonCol>
                                                            <IonCol size='4' className="my-auto">
                                                                <IonButtons className='ion-float-right'>
                                                                    <IonButton onClick={() => { routeToPage(page) }} className="ipro-btn" color="primary" fill='clear'>
                                                                        {/* Edit */}
                                                                        <IonIcon slot="icon-only" icon={createOutline} />
                                                                    </IonButton>
                                                                    {logInfo.Role?.includes(ROLES.iProEmployee) &&
                                                                        <IonButton onClick={() => { setSelectedPage(page); setShowLambdaEditorModal(true) }}>
                                                                            {/* Lambdas */}
                                                                            <IonIcon slot="icon-only" icon={codeSlashOutline}></IonIcon>
                                                                        </IonButton>
                                                                    }       
                                                                    <IonButton disabled={isViewMode} onClick={e => onDeletePage(e, page)} color="danger" fill='clear'>
                                                                        {/* Delete */}
                                                                        <IonIcon slot="icon-only" icon={trashOutline} />
                                                                    </IonButton>
                                                                    {!isViewMode && (
                                                                        <IonReorder slot="start">
                                                                            <IonIcon size="large" icon={appsOutline} />
                                                                        </IonReorder>
                                                                    )}
                                                                </IonButtons>
                                                            </IonCol>
                                                        </IonRow>
                                                    </IonGrid>
                                                </IonCardHeader>
                                                {page.Collapsed && (
                                                    <FadeIn>
                                                        <IonCardContent>
                                                            <IonGrid className="ion-no-padding">
                                                                <IonRow className="ion-no-padding">
                                                                    <IonCol size='6'>
                                                                        <IonItem lines="inset">
                                                                            <IonLabel className='input-title' position="stacked">Name</IonLabel>
                                                                            <IonInput disabled={isViewMode} type="text" id="Name" onIonBlur={(e: any) => { updatePage(e, page) }} value={page.Name} clearInput></IonInput>
                                                                        </IonItem>
                                                                    </IonCol>
                                                                    <IonCol size='6'>
                                                                        <IonItem lines="inset">
                                                                            <IonLabel className='input-title' position="stacked">Continue to</IonLabel>
                                                                            <IonSelect 
                                                                                id="NextPageId" 
                                                                                placeholder="Next Page(Default)"
                                                                                disabled={isViewMode}  
                                                                                value={page.NextPageId} 
                                                                                interface="popover"
                                                                                onIonChange={(e: any) => { updatePage(e, page) }} 
                                                                                >
                                                                                <IonSelectOption key={'NA'} value={0}>Next Page(Default)</IonSelectOption>
                                                                                {questionnaire?.PhqSelected?.Pages!.filter(p => p !== page).map(p =>
                                                                                    <IonSelectOption key={p.PageID} value={p.PageID}>{p.Name}</IonSelectOption>
                                                                                )}
                                                                            </IonSelect>
                                                                        </IonItem>
                                                                    </IonCol>
                                                                </IonRow>
                                                                <IonRow className="ion-no-padding">
                                                                    <IonCol size='12' className="ion-no-padding">
                                                                        <IonItem lines="inset">
                                                                            <IonLabel className='input-title' position="stacked">Description</IonLabel>
                                                                            <IonTextarea disabled={isViewMode} autoGrow={true} id="Label" onIonBlur={(e) => { updatePage(e, page) }} value={page.Label} ></IonTextarea>
                                                                        </IonItem>
                                                                    </IonCol>
                                                                </IonRow>
                                                            </IonGrid>
                                                        </IonCardContent>
                                                    </FadeIn>
                                                )}
                                            </IonCard>
                                        )
                                    )
                                })}
                            </IonReorderGroup>
                            )
                            : (
                                <IonGrid>
                                    <IonRow>
                                        <IonCol className="ion-text-center">
                                            No Data
                                        </IonCol>
                                    </IonRow>
                                    <IonRow>
                                        <IonCol className="ion-text-center">
                                            <IonButton disabled={isViewMode} color="primary" onClick={() => {
                                                addNewPage()
                                            }} fill="outline"> New Page
                                            </IonButton>
                                        </IonCol>
                                    </IonRow>
                                </IonGrid>
                            )
                        }
                <RemoveAlert isOpen={showDeleteAlert} onCancel={() => setShowDeleteAlert(false)} onDelete={() => deletePage()} />
                
                <IonModal isOpen={showPhqSelectedModal} onDidDismiss={() => setShowPhqSelectedModal(false)}>
                    <IonHeader>
                        <IonToolbar>
                                <IonTitle color="primary" className="ion-text-center">
                                    {questionnaire.PhqSelected?.Label}
                                </IonTitle>
                                <IonButtons slot="end">
                                    <IonButton size="small" color="danger" onClick={ e => { setShowPhqSelectedModal(false); e.stopPropagation() }}>
                                        <IonIcon slot="icon-only" icon={closeCircleOutline} />
                                    </IonButton>
                                </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent>
                        <IonItem >
                            <IonLabel position='floating'> Label</IonLabel>
                            <IonInput id="Label" debounce={300} onIonBlur={(e: any) => { updatePHQ_blur(e, questionnaire.PhqSelected) }} value={questionnaire.PhqSelected?.Label}></IonInput>
                        </IonItem>
                        <IonItem>
                            <IonLabel position='floating'> Description</IonLabel>
                            <IonTextarea id="Description" debounce={300} autoGrow={true} onIonBlur={(e: any) => { updatePHQ_blur(e, questionnaire.PhqSelected) }} value={questionnaire.PhqSelected?.Description}></IonTextarea>
                        </IonItem>
                    </IonContent>
                    <IonFooter>
                        <IonToolbar>
                            <IonButtons>
                                <IonButton fill="solid" className="w-100" onClick={() => setShowPhqSelectedModal(false)}>Save</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                </IonModal>
                <IonModal isOpen={showLambdaEditorModal} onDidDismiss={() => setShowLambdaEditorModal(false)} cssClass='lambda-editor-modal'>
                    <LambdaEditor page={selectedPage!} onCancelModal={() => setShowLambdaEditorModal(false)} />
                </IonModal>
            </IonContent>
        <Footer />
    </IonPage>
    )
};

export default PHQPage;
